import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { parseUserAgent } from '@/lib/userAgent';

const SESSION_KEY = 'tracking_session_id';
const LANDED_KEY = 'tracking_landed';

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-view`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const isInternalHost = () => {
  const h = window.location.hostname;
  return /(^|\.)(lovable\.app|lovable\.dev|lovableproject\.com)$/i.test(h);
};

export const usePageTracking = () => {
  useEffect(() => {
    if (isInternalHost()) return;
    let viewId: string | null = null;
    let startedAt = Date.now();
    let sent = false;

    const url = new URL(window.location.href);
    const utm_source = url.searchParams.get('utm_source');
    const utm_medium = url.searchParams.get('utm_medium');
    const utm_campaign = url.searchParams.get('utm_campaign');

    const isLanding = !sessionStorage.getItem(LANDED_KEY);
    sessionStorage.setItem(LANDED_KEY, '1');

    const ua = parseUserAgent(navigator.userAgent);

    const sendView = async () => {
      try {
        const res = await fetch(FUNCTIONS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
          body: JSON.stringify({
            type: 'view',
            page_url: window.location.pathname + window.location.hash,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            session_id: getSessionId(),
            device_type: ua.device_type,
            browser: ua.browser,
            os: ua.os,
            utm_source,
            utm_medium,
            utm_campaign,
            is_landing: isLanding,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          viewId = data.id;
          startedAt = Date.now();
        }
      } catch {
        // fallback: direct insert (no geo)
        try {
          const { data } = await supabase
            .from('page_views')
            .insert({
              page_url: window.location.pathname + window.location.hash,
              referrer: document.referrer || null,
              user_agent: navigator.userAgent,
              session_id: getSessionId(),
              device_type: ua.device_type,
              browser: ua.browser,
              os: ua.os,
              utm_source,
              utm_medium,
              utm_campaign,
              is_landing: isLanding,
            })
            .select('id')
            .single();
          if (data) {
            viewId = data.id;
            startedAt = Date.now();
          }
        } catch {
          // silently fail
        }
      }
    };

    const sendDuration = () => {
      if (sent || !viewId) return;
      sent = true;
      const duration_ms = Date.now() - startedAt;
      try {
        const payload = JSON.stringify({ type: 'duration', view_id: viewId, duration_ms });
        const blob = new Blob([payload], { type: 'application/json' });
        // sendBeacon ignores custom headers; edge function still accepts it (CORS not enforced on beacon)
        navigator.sendBeacon(FUNCTIONS_URL, blob);
      } catch {
        // ignore
      }
    };

    sendView();

    const onHide = () => {
      if (document.visibilityState === 'hidden') sendDuration();
    };
    window.addEventListener('pagehide', sendDuration);
    document.addEventListener('visibilitychange', onHide);

    return () => {
      window.removeEventListener('pagehide', sendDuration);
      document.removeEventListener('visibilitychange', onHide);
      sendDuration();
    };
  }, []);
};
